/**
 * Easy scenery drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easySceneryDrawingsPack: ToilePack = {
    slug: 'easy-scenery-drawings',
    category: 'coloring',
    name: 'Easy scenery drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'village', label: 'Village', subject: 'a village scene with a hut under a coconut tree, a curving river, two hills behind and a rising sun between them'},
        {key: 'mountain', label: 'Mountains', subject: 'a mountain scene with three snowy peaks, a pine forest, a small lake in front and a wooden cabin on its shore'},
        {key: 'beach', label: 'Beach', subject: 'a beach scene with a palm tree, gentle waves, a sailboat on the horizon and a setting sun with birds'},
    ],
    styles: [
        {key: 'outline', label: 'Clean outline', style: 'one bold uniform black outline, simple rounded shapes and large open areas to color', palette: 'Black ink only, no shading, no gray tones.', framing: 'The scene fills the landscape page with a small white margin.'},
        {key: 'pencil', label: 'Pencil', style: 'a light pencil outline with visible loose strokes and a few construction lines left in', palette: 'Graphite gray lines on white paper.', framing: 'The scene fills the landscape page with a small white margin.'},
        {key: 'hatched', label: 'Ink hatching', style: 'a fine black ink outline with light parallel hatching on the hills, water and shadows', palette: 'Black ink only.', framing: 'The scene fills the landscape page with a small white margin.'},
    ],
    directions: ['autumn', 'at night', 'desert'],
    directionHint: 'try: autumn',
    seo: {
        title: 'Easy Scenery Drawings: Village, Mountains, Beach',
        metaDescription: 'Nine simple landscape drawings, a village, mountains and a beach, in bold outline, pencil and ink hatching for students to copy.',
        h1: 'Easy scenery drawings: village, mountains, beach',
        tagline: 'Simple scenes in clear outline, easy to copy and then color.',
        intro: 'Easy scenery drawings are the landscapes students draw for an art class: a few hills, a house, a river and a sun. These nine pages show simple scenes in clear outline: a village, mountains and a beach. Each scene comes as a bold outline, a light pencil copy or an ink study with hatching. For another scene, type it in the direction box.',
        useCases: [
            'A drawing lesson for a class',
            'A page to color for a child',
            'A model for a sketchbook copy',
            'A scene for a drawing book',
        ],
        faq: [
            {q: 'Do the three styles show the same scenes?', a: 'Yes. The clean outline, pencil and ink hatching styles each draw the same three scenes: the village, the mountains and the beach.'},
            {q: 'Which style suits coloring?', a: 'The clean outline style has one bold black line and large open areas, which suits coloring.'},
            {q: 'Can I ask for a different scene?', a: 'Yes. Type it in the direction box, such as autumn, at night or desert. A typed scene is a request, so the result may differ from the samples.'},
        ],
    },
}
